import React from 'react';
import * as Sentry from '@sentry/react';
import { errorHandler } from '../utils/error-handler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    errorHandler(error, {
      tags: {
        component: this.props.name || 'ErrorBoundary',
        errorBoundary: true,
      },
      extra: {
        errorInfo,
        props: this.props,
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h3>Something went wrong</h3>
            <p>We&apos;ve been notified about this error and are working to fix it.</p>
            <button className="btn btn-primary" onClick={this.handleRetry}>
              Try Again
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre>{this.state.error?.stack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export const withErrorBoundary = (Component, name) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary name={name || Component.displayName || Component.name}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${name || Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Sentry's built-in error boundary (recommended)
export const SentryErrorBoundary = Sentry.withErrorBoundary;

export default ErrorBoundary;
